
<script setup>

  import { ref, computed, watch } from 'vue';

  import { useRoute } from 'vue-router';

  import { TransitionRoot, TransitionChild, Dialog, DialogPanel, DialogTitle, DialogDescription } from '@headlessui/vue';

  import { ExclamationTriangleIcon } from '@heroicons/vue/24/solid';

  const route = useRoute();

  const activity = computed( () => {
    
    const match = route.path.match( /^\/activity\/(\d+)\/?/ );

    return match ? parseInt( match[ 1 ] ) : null;
  
  } );

  const selection = ref( null );

  watch( () => route.path, () => selection.value = activity.value );

  const isOpen = ref( false );

  const promise = ref( null );

  let promiseResolve, promiseReject;

  function ask() {
    
    isOpen.value = true;

    return promise.value = new Promise( ( resolve, reject ) => {

      promiseResolve = resolve;

      promiseReject = reject;

    } );

  }

  function resolve() {
    
    isOpen.value = false;

    if ( promiseResolve ) promiseResolve( selection.value );

  }

  function reject() {
    
    isOpen.value = false;

    if ( promiseReject ) promiseReject();

  }

  defineExpose( { ask, resolve, reject } );

</script>

<template>

  <TransitionRoot appear :show="isOpen" as="template">

    <Dialog as="div" @close="reject" class="relative z-20">

      <TransitionChild

        as="template"

        enter="transition-opacity duration-300 ease-out"

        enter-from="opacity-0"

        enter-to="opacity-100"

        leave="transition-opacity duration-200 ease-in"

        leave-from="opacity-100"

        leave-to="opacity-0">

        <div class="fixed inset-0 bg-black bg-opacity-75" />

      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">

        <div class="flex min-h-full items-center justify-center p-6 drop-shadow-[0_0_15px_rgba(0,0,0,1)]">

          <TransitionChild

            as="template"

            enter="transition transform duration-300 ease-out"

            enter-from="opacity-0 scale-95"

            enter-to="opacity-100 scale-100"

            leave="transition transform duration-200 ease-in"

            leave-from="opacity-100 scale-100"

            leave-to="opacity-0 scale-95">

            <DialogPanel class="w-full max-w-xl bg-white overflow-hidden">
              
              <DialogTitle class="flex items-center p-4 bg-neutral-100">

                <ExclamationTriangleIcon class="w-9 text-red-600" />

                <div class="ml-4 uppercase text-xl font-bold">Clear &amp; Start Over</div>

              </DialogTitle>

              <div class="px-12 py-8">

                <DialogDescription class="leading-7 text-neutral-700">

                  You can clear your work if you're sharing a device

                  with others. Be sure to download your activity file at

                  the end of each activity before clearing your work.

                </DialogDescription>

                <div class="flex items-center mt-8">

                  <input id="selection-activity" name="selection" :value="activity" type="radio" v-model="selection" :disabled="! activity"
                  
                    class="h-4 w-4 border-neutral-300 text-neutral-600 focus:ring-lime-800" />

                  <label for="selection-activity" :class="[ 'ml-3 block font-medium leading-6', { 'text-neutral-400': ! activity } ]">
                    
                    Clear on <strong>this activity</strong>.
                  
                  </label>

                </div>

                <div class="flex items-center mt-4">

                  <input id="selection-all" name="selection" :value="null" type="radio" v-model="selection"
                  
                    class="h-4 w-4 border-neutral-300 text-neutral-600 focus:ring-lime-800" />

                  <label for="selection-all" class="ml-3 block font-medium leading-6">Clear <strong>all</strong> activities.</label>

                </div>

                <div class="flex flex-wrap items-center justify-between mt-10">

                  <button
                  
                    type="button" @click="reject"
                  
                    class="inline-flex items-center gap-4 px-6 py-4 text-white font-bold text-white bg-neutral-500 transition-colors
            
                      hover:bg-neutral-600 focus-visible:bg-neutral-800 hover:underline focus-visible:underline">

                    <span>Cancel</span>

                  </button>

                  <button
                  
                    type="button" @click="resolve"

                    class="inline-flex items-center gap-4 px-6 py-4 text-white font-bold text-white bg-red-600 transition-colors
            
                      hover:bg-red-700 focus-visible:bg-red-700 hover:underline focus-visible:underline">

                    <span>Clear</span>

                  </button>

                </div>

              </div>

            </DialogPanel>

          </TransitionChild>

        </div>

      </div>

    </Dialog>

  </TransitionRoot>

</template>
